import React, { useState } from 'react';
import { Box, ButtonBase, Radio, Typography } from '@mui/material';
import BackdropDialog from 'common/components/BackdropDialog';
import { ShopManualOnlinePaymentMethodObject } from 'common/modules/payments/types';
import { useSelector } from 'react-redux';
import * as LanguageSelectors from 'selectors/LanguageSelectors';
import { useTranslation } from 'services/localization';
import { getTranslation } from 'common/modules/translations';
import { notNull } from 'common/utils/common';
import { getPaymentMethodId } from 'common/modules/payments/paymentMethods';

interface ListOption {
	value: string;
	label: string;
	description: string;
	method: ShopManualOnlinePaymentMethodObject;
}

interface Props {
	isOpen: boolean;
	onClose: () => void;
	currentValue: ShopManualOnlinePaymentMethodObject;
	onChange: (value: ShopManualOnlinePaymentMethodObject) => void;
	options: ShopManualOnlinePaymentMethodObject[];
}
const ManualPaymentMethodDialog = (props: Props) => {
	const { t } = useTranslation();
	const { currentValue, options, onClose, isOpen, onChange } = props;
	const lang = useSelector(LanguageSelectors.language);
	const [value, setValue] = useState<ShopManualOnlinePaymentMethodObject>(currentValue);

	const listOptions: ListOption[] = (() => {
		return options
			.map((method): ListOption | null => {
				switch (method.id) {
					case 'MANUAL': {
						return {
							value: method.details.id,
							label: getTranslation(method.details.label, lang),
							description: getTranslation(method.details.description, lang),
							method,
						};
					}
					case 'PAY_STORE': {
						return {
							value: 'PAY_STORE',
							label: t('payments.payInstore'),
							description: '',
							method,
						};
					}
					default:
						return null;
				}
			})
			.filter(notNull)
			.sort((a, _b) => {
				if (a.value === 'PAY_STORE') return -1;
				return 0;
			});
	})();

	const isOptionSelected = (option: ListOption) => {
		const optionId = getPaymentMethodId(value);
		return option.value === optionId;
	};

	const handleOptionSelect = (option: ListOption) => {
		setValue(option.method);
	};

	const handleClose = (action: 'confirm' | 'cancel' | 'click_away' | 'left-action') => {
		switch (action) {
			case 'confirm': {
				onChange(value);
				onClose();
				break;
			}
			case 'cancel':
			case 'click_away':
			case 'left-action': {
				onClose();
				break;
			}
			default:
				break;
		}
	};

	const renderDisabledCurrentValue = () => {
		if (
			currentValue.id === 'MANUAL' &&
			!options.find((o) => o.id === 'MANUAL' && o.details.id === currentValue.details.id)
		) {
			return renderOption(
				{
					value: currentValue.details.id,
					label: getTranslation(currentValue.details.label, lang),
					description: getTranslation(currentValue.details.description, lang),
					method: currentValue,
				},
				{
					disabled: true,
				},
			);
		}
		return null;
	};

	const renderOption = (option: ListOption, options?: { disabled?: boolean }) => {
		return (
			<ButtonBase
				key={option.value}
				onClick={() => handleOptionSelect(option)}
				sx={{ p: 1, mb: 2, width: '100%' }}
				focusRipple
				disabled={options?.disabled}
			>
				<Box
					sx={{
						display: 'flex',
						flexDirection: 'row',
						justifyContent: 'flex-start',
						alignItems: 'flex-start',
						textAlign: 'left',
						width: '100%',
					}}
				>
					<Radio checked={isOptionSelected(option)} tabIndex={-1} disabled={options?.disabled} />
					<Box
						sx={{
							mt: 1,
							display: 'flex',
							flexDirection: 'column',
							alignItems: 'flex-start',
						}}
					>
						<Typography variant="body1" sx={{ opacity: options?.disabled ? 0.5 : 1 }}>
							{option.label}
						</Typography>
						<Typography
							variant="body2"
							color="secondary"
							sx={{ opacity: options?.disabled ? 0.5 : 1 }}
						>
							{option.description}
						</Typography>
					</Box>
				</Box>
			</ButtonBase>
		);
	};

	return (
		<BackdropDialog
			isOpen={isOpen}
			label={t('payments.selectPaymentMethod', 'Select payment method')}
			onClose={handleClose}
			body={
				<Box>
					{renderDisabledCurrentValue()}
					{listOptions.map((option) => {
						return renderOption(option);
					})}
				</Box>
			}
			confirm={{
				label: t('common:actions.save', 'Save'),
			}}
			cancel={{
				label: t('common:actions.cancel', 'Cancel'),
			}}
		></BackdropDialog>
	);
};

export default ManualPaymentMethodDialog;
